import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, Grid, Paper, Chip, useTheme, useMediaQuery } from "@mui/material";
import { withRouter } from "react-router";
import { observer } from "mobx-react-lite";
import StyledPage from "../../common/StyledPage";
import StyledSubHeader from "../../common/StyledSubHeader";
import StyledProgressCircle from "../../common/StyledProgressCircle";
import { deleteSiteContent, getCategoriesForContent, getSiteContentArticle, hideSiteContent, publishSiteContent } from "../../../actions/internal/SiteActions";
import { getSiteContentByTrackingId } from "../../../selectors/SiteSelectors";
import { getUserId } from "../../../selectors/UserSelectors";
import ReactMarkdown from 'react-markdown';
import StyledSection from "../../common/StyledSection";
import { TextButton } from "../../common/StyledButtons";
import { GENERATION_STATUS, GENERATION_STATUS_LOOKUP } from "../../../constants/Statuses";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { CARD_HEADER_STYLE, SUMMARY_TITLE_FONT } from '../../../constants/Styles';
import ConfirmationModal from "../modals/ConfirmationModal";
import { DeleteOutline, Edit, Publish, VisibilityOff } from "@mui/icons-material";
import { formatDateTimeToFancyString, formatDateToFancyString } from "../../../utilities/DateUtilities";
import PublishOptionsModal from "../modals/PublishOptionsModal";
import Colors from "../../../constants/Colors";
import EditContentDetailsModal from "../modals/EditContentDetailsModal";
import { isAdmin } from "../../../actions/profile/UserActions";
import StyledAccordion, { StyledAccordionItem } from "../../common/StyledAccordion";

const ViewSiteContentDetailsPage = ({ match, history }) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const categoriesRef = useRef([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showConfirmPublish, setShowConfirmPublish] = useState(false);
  const [showConfirmHide, setShowConfirmHide] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showEditArticle, setShowEditArticle] = useState(false);

  const siteId = match.params.site_id;
  const trackingId = match.params.article_id;
  const article = getSiteContentByTrackingId(trackingId);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getSiteContentArticle(getUserId(), siteId, trackingId);
      const resp = await getCategoriesForContent(getUserId(), siteId);
      categoriesRef.current = resp.ok ? resp.categories : [];
    } catch (ex) {
      console.error(ex);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublish = async () => {
    try {
      setShowConfirmPublish(false)
      showNotificationBar(NOTIFICATION_TYPE.INFO, 'Publishing article');
      setIsPublishing(true);

      // Prepare options
      const options = {};
      if (article.wp_details && article.wp_details.category_ids && article.wp_details.category_ids.length > 0) {
        options.categoryIds = article.wp_details.category_ids;
      }

      await publishSiteContent(getUserId(), siteId, article.content_id, trackingId, options);
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Article published');
    } catch(ex) {
      showNotificationBar(NOTIFICATION_TYPE.ERROR, 'Failed to publish this article, please try again later.');
    } finally {
      setIsPublishing(false);
    }
  }

  const handleHideContent = async () => {
    try {
      setShowConfirmHide(false);
      setIsPublishing(true);
  
      await hideSiteContent(getUserId(), siteId, trackingId);
  
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Article has been scheduled');
    } catch (ex) {
      showNotificationBar(
        NOTIFICATION_TYPE.ERROR,
        'Failed to publish this article, please try again later.'
      );
    } finally {
      setIsPublishing(false);
    }
  }

  const handleDeleteContent = async () => {
    try {
      setShowConfirmDelete(false);
      setIsLoading(true);
  
      await deleteSiteContent(getUserId(), siteId, trackingId);
      history.replace(`/dashboard/site/${siteId}/content`);
  
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Article has been deleted.');
    } catch (ex) {
      showNotificationBar(
        NOTIFICATION_TYPE.ERROR,
        'Failed to delete this article, please try again later.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  const handleSchedulePublish = async (date) => {
    try {
      setShowConfirmPublish(false);
      showNotificationBar(NOTIFICATION_TYPE.INFO, 'Scheduling article');
      setIsPublishing(true);
  
      // Prepare scheduling options for endpoint
      const options = {
        publish_now: false,
        schedule_date: date ? new Date(date).toISOString() : null
      };

      if (article.wp_details && article.wp_details.category_ids && article.wp_details.category_ids.length > 0) {
        options.category_ids = article.wp_details.category_ids;
      }
  
      await publishSiteContent(
        getUserId(),
        siteId,
        article.content_id,
        trackingId,
        options
      );
  
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, 'Article has been scheduled');
    } catch (ex) {
      showNotificationBar(
        NOTIFICATION_TYPE.ERROR,
        'Failed to publish this article, please try again later.'
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const handleGoToEdit = () => {
    const siteId = match.params.site_id;
    const docId = match.params.article_id;
  
    history.push(`/dashboard/site/${siteId}/content/${docId}/edit`);
  };

  const renderLoading = () => <StyledProgressCircle />;

  const renderDescription = (text) =>
    text.split("\n").reduce((acc, val, i) => [...acc, <div key={i}>{val}</div>], []);

  const renderArticleCard = () => (
    <StyledSection
    title="Article"
    rightAdornment={
      (
        <TextButton
        onClick={handleGoToEdit}
        sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
      >
        <Edit fontSize="small" sx={{ marginRight: '4px' }} />
        Edit
      </TextButton>
      )
    }>
      <Box sx={{ marginTop: '24px' }}>
        <Box
            sx={{
            border: '1px solid #ddd',
            borderRadius: '12px',
            padding: '24px 48px',
            backgroundColor: '#fff',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
            }}
        >
            <Box
            className="markdown-content"
            sx={{
                '& h1': { fontSize: '1.9rem', fontWeight: 600, mt: 2, mb: 1 },
                '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 2, mb: 1, textTransform: 'none' },
                '& h3': { fontSize: '1.15rem', fontWeight: 600, mt: 1.5, mb: 0.8, textTransform: 'none' },
                '& h4': { fontSize: '1rem', fontWeight: 500, mt: 1.2, mb: 0.6, textTransform: 'none' },
                '& h5': { fontSize: '0.9rem', fontWeight: 500, mt: 1, mb: 0.5, textTransform: 'none' },
                '& p': { fontSize: '0.95rem', lineHeight: 1.6, mb: 1, textTransform: 'none' },
                '& ul, & ol': { fontSize: '0.95rem', ml: 3 },
                '& code': {
                fontSize: '0.85rem',
                backgroundColor: '#f5f5f5',
                px: 0.5,
                py: 0.2,
                borderRadius: '4px',
                fontFamily: 'monospace',
                },
            }}
            >
            <ReactMarkdown>{(article && article.content.final_draft.draft_article) || ''}</ReactMarkdown>
            </Box>
        </Box>
      </Box>
    </StyledSection>
  );

  const renderOverviewButton = (status) => {
    if (isPublishing) {
      return <StyledProgressCircle />
    }
    switch (status) {
      case GENERATION_STATUS.SUCCESS:
        return (
          <TextButton
            sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
            onClick={() => setShowConfirmPublish(true)} 
          >
            <Publish fontSize="small" sx={{ marginRight: '4px' }} />
            Publish
          </TextButton>
        )
      case GENERATION_STATUS.PUBLISHED:
        return (
          <TextButton
            sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
            onClick={() => setShowConfirmHide(true)}
          >
            <VisibilityOff fontSize="small" sx={{ marginRight: '4px' }} />
            Hide Article
          </TextButton>
        )
      default:
        return null;
    }
  }

  const buildArticleStatus = (metadata) => {
    const timestamp = metadata.published_timestamp;
  
    if (!timestamp) {
      return {
        label: 'Status',
        value: GENERATION_STATUS_LOOKUP[article.generator_status].label
      };
    }
  
    const publishDate = new Date(timestamp);
    const now = new Date();
  
    const isFuture = publishDate.getTime() > now.getTime();
  
    return {
      label: 'Status',
      value: isFuture ? 'Scheduled' : GENERATION_STATUS_LOOKUP[article.generator_status].label
    };
  }

  const buildPublishDate = (metadata) => {
    const timestamp = metadata.published_timestamp;
  
    if (!timestamp) {
      return {
        label: 'Published On',
        value: <em>No Date Set</em>
      }
    }
  
    const publishDate = new Date(timestamp);
    const now = new Date();
  
    const isFuture = publishDate.getTime() > now.getTime();
  
    return {
      label: isFuture ? 'Scheduled For' : 'Published On',
      value: formatDateTimeToFancyString(timestamp)
    };
  }

  const buildCategories = () => {
    if (
      !article ||
      !article.wp_details ||
      !Array.isArray(article.wp_details.category_ids) ||
      article.wp_details.category_ids.length === 0
    ) {
      return <em>Uncategorized</em>;
    }
  
    const names = article.wp_details.category_ids
      .map(id => {
        const item = categoriesRef.current.find(cat => cat.id === id);
        if (item) return item.name;
        return undefined;
      })
      .filter(Boolean);
  
    if (names.length === 0) {
      return <em>Uncategorized</em>;
    }
  
    return names.join(", ");
  };

  const renderOverviewCard = () => {
    if (!article) {
      return (
        <StyledSection title="Overview">
          <em>No article available</em>
        </StyledSection>
      );
    }
  
    const metadata = article.metadata || {};
    const content = article.content || {};
    const generatorStatus = article.generator_status;
  
    const overviewItems = [
      buildArticleStatus(metadata),
      {
        label: 'Created On',
        value: metadata.generation_start_timestamp
          ? formatDateTimeToFancyString(metadata.generation_start_timestamp)
          : <em>No creation date</em>,
      },
      buildPublishDate(metadata),
    ];
  
    return (
      <StyledSection
        title="Overview"
        rightAdornment={generatorStatus ? renderOverviewButton(generatorStatus) : undefined}
      >
        <Grid container spacing={2}>
          {overviewItems.map((item, idx) => (
            <Grid item xs={12} sm={4} key={idx}>
              <Box
                sx={{
                  minHeight: isMobile ? 'auto' : '100px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <h4 style={{ fontSize: '14px', marginBottom: '4px', marginTop: 0 }}>
                  {item && item.label ? item.label : 'N/A'}
                </h4>
                <h3 style={{ fontSize: '18px', margin: 0 }}>
                  {item && item.value ? item.value : <em>Not available</em>}
                </h3>
              </Box>
              {isMobile && idx !== 2 && <Divider />}
            </Grid>
          ))}
        </Grid>
  
        <Box sx={{ marginTop: '12px' }}>
          <div style={{ marginTop: '30px', marginBottom: '18px' }}>
            <span style={{ paddingRight: '40px', ...SUMMARY_TITLE_FONT }}>
              Article Topic
            </span>
            {content.topic ? content.topic : <em>No topic available</em>}
          </div>
  
          <Divider />
  
          <div style={{ marginTop: '18px', marginBottom: '18px' }}>
            <span style={{ paddingRight: '30px', ...SUMMARY_TITLE_FONT }}>
              Target Phrase
            </span>
            {content.primary_keyword ? content.primary_keyword : <em>No target phrase available</em>}
          </div>
  
          <Divider />
  
          <Box sx={{ marginTop: '14px' }}>
            <StyledAccordionItem
              title="Supporting Article Details"
              details={renderDescription(content.brief ? content.brief : 'No summary provided.')}
              titleStyling={SUMMARY_TITLE_FONT}
            />
          </Box>
        </Box>
      </StyledSection>
    );
  };

  const renderContentDetailsCard = () => (
    <StyledSection
      title="Content Details"
      rightAdornment={
      <TextButton
        onClick={() => setShowEditArticle(true)}
        sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
      >
        <Edit fontSize="small" sx={{ marginRight: '4px' }} />
        Edit
      </TextButton>}
    >
      <Box sx={{ marginTop: "12px" }}>
        <div style={{marginTop: '18px', marginBottom: '18px' }}><span style={{paddingRight: '65px', ...SUMMARY_TITLE_FONT }}>Category</span>{buildCategories()}</div>
      </Box>
    </StyledSection>
  );

  const prepareTools = () => {
    const tools = []

    
    tools.push({
        name: "Delete Article",
        icon: <DeleteOutline sx={{ color: Colors.primaryColor, fontSize: '64px' }} />,
        description: 'Remove this article permanently.',
        onClick: () => setShowConfirmDelete(true),
        buttonText: "Request"
    })

    return tools;
  }

  const renderToolCards = () => (
    <StyledSection
        title="Actions"
    >  
        <Grid container item xs={12} spacing={1}>
            {    
            prepareTools().map((tool) => (
              <Grid item xs={12} key={tool.name}>
                  <Paper
                  variant="outlined"
                  onClick={tool.onClick}
                  sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      height: '100%',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                      '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          transform: 'scale(1.02)',
                        },
                  }}
                  >
                  {/* Left: Icon + Text */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                      <Box>{tool.icon}</Box>
                      <Box>
                      <div style={CARD_HEADER_STYLE}>{tool.name}</div>
                      <p style={{ margin: 0 }}>{tool.description}</p>
                      </Box>
                  </Box>
                  
                  </Paper>
              </Grid>
              ))}
        </Grid>
    </StyledSection>
  )


  return (
    <StyledPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledSubHeader title="Article Details" />
        </Grid>
        <Grid item xs={12}>
          {isLoading ? renderLoading() : 
          <>
            {renderOverviewCard()}
            {renderContentDetailsCard()}
            {renderArticleCard()}
            {isAdmin() && renderToolCards()}
          </>
          }
        </Grid>
      </Grid>
      <PublishOptionsModal
        open={showConfirmPublish}
        onClose={() => setShowConfirmPublish(false)}
        onPublishNow={handlePublish}
        onSchedule={handleSchedulePublish}
      />
      <EditContentDetailsModal
        open={showEditArticle}
        onClose={() => setShowEditArticle(false)}
        article={article}
        categories={categoriesRef.current}
      />
      <ConfirmationModal
        open={showConfirmHide}
        onCancel={() => setShowConfirmHide(false)}
        title="Hide Article"
        description={"Are you sure you would like to hide this article from public view?"}
        okButtonText='Hide'
        cancelButtonText='Cancel'
        onOk={handleHideContent}
      />
      <ConfirmationModal
        open={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        title="Delete Article"
        description={"Are you sure that you would like to delete this article? This action cannot be undone."}
        okButtonText='Delete'
        cancelButtonText='Cancel'
        onOk={handleDeleteContent}
      />

    </StyledPage>
  );
};

export default observer(withRouter(ViewSiteContentDetailsPage));
